import { useNavigate, useParams } from 'react-router'
import Spinner from '@cloudscape-design/components/spinner'
import Box from '@cloudscape-design/components/box'
import Page from '@/components/common/Page'
import { AgentSetupWizard } from '@/pages/clients/ClientAgent/AgentSetupWizard'
import { useAgentStatus } from '@/hooks/useAgentStatus'
import { useClient } from '@/hooks/useClients'

function utcTimeToLocal(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const d = new Date()
  d.setUTCHours(h, m, 0, 0)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export default function AgentSetupPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { goal, persona, loading: statusLoading } = useAgentStatus(clientId ?? '')
  const { client, loading: clientLoading } = useClient(clientId ?? '')

  if (!clientId) return null

  if (statusLoading || clientLoading) {
    return (
      <Page title="Agent Setup">
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      </Page>
    )
  }

  const ag = client?.schedulingConfig?.autoGenerate
  const initialValues = {
    businessType: persona?.focusAreas?.[0] ?? '',
    goalType: goal?.goalType ?? 'grow_referrals',
    targetValue: String(goal?.targetValue ?? '10'),
    channels: (ag?.contentTypes?.length ? ag.contentTypes : client?.selectedContentTypes) ?? ['facebook_post', 'linkedin_post'],
    autoGenerate: ag?.enabled ?? true,
    cadence: ag?.cadence ?? 'daily',
    timeLocal: ag?.timeUtc ? utcTimeToLocal(ag.timeUtc) : '09:00',
    personaName: persona?.name ?? 'Alex',
  }

  return (
    <Page
      title="Agent Setup"
      helpContent={{
        header: 'Agent Setup Wizard',
        body: (
          <div>
            <p>Step-by-step wizard to configure your marketing agent's behavior and scheduling.</p>
            <h4>Setup Steps:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li>Define your business goals and target outcomes</li>
              <li>Configure agent persona and communication style</li>
              <li>Select content channels to publish to</li>
              <li>Set up auto-generation schedule and frequency</li>
            </ul>
            <h4>Tips:</h4>
            <p>Complete all steps for best results. You can return to this setup anytime to update agent configuration from the Configure page.</p>
          </div>
        ),
      }}
    >
      <AgentSetupWizard
        clientId={clientId}
        initialValues={initialValues}
        onComplete={() => navigate(`/workspace/clients/${clientId}/agent`)}
        onCancel={() => navigate(`/workspace/clients/${clientId}/agent`)}
      />
    </Page>
  )
}
